import fbemitter from 'fbemitter';
import { v4 as uuid } from 'uuid';

import { warn } from '~/src/dash/logging/logging';

export enum EUploadResponseType {
  ADDED,
  PROGRESS,
  SUCCESS,
  FAILURE,
  ABORTED,
  DELETED,
}

function formatBytes(bytes: number, decimals: number = 2) {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export interface IUploadInfo {
  status: EUploadResponseType;
  totalSize: number;
  estimatedTimeLeft: number;
  uploadedSize: number;
  message?: string;
}

export interface IUploadStatus {
  status: EUploadResponseType;
  key: string;
  name: string;
}
export interface IUploadInfoBeautify {
  status: EUploadResponseType;
  totalSize: string;
  estimatedTimeLeft: string;
  uploadedSize: string;
  progress: number;
  message: string;
}

export function beautifyUploadProgress(infos: IUploadInfo): IUploadInfoBeautify {
  const out: IUploadInfoBeautify = {
    progress: 0,
    estimatedTimeLeft: '',
    totalSize: '',
    uploadedSize: '',
    status: EUploadResponseType.PROGRESS,
    message: infos.message ? infos.message : '',
  };

  out.totalSize = formatBytes(infos.totalSize);
  out.uploadedSize = formatBytes(infos.uploadedSize);
  out.progress = Math.round((infos.uploadedSize / infos.totalSize) * 100);
  out.status = infos.status;

  return out;
}

interface IUploadPair {
  name: string;
  xhr: XMLHttpRequest | undefined;
  infos: IUploadInfo;
}

export default class UploadManager {
  events = new fbemitter.EventEmitter();

  uploads = new Map<string, IUploadPair>();

  cancelUpload(key: string): boolean {
    const obj = this.uploads.get(key);
    if (obj === undefined || obj.xhr === undefined) {
      return false;
    } else {
      obj.xhr.abort();

      this.uploads.set(key, {
        ...obj,
        infos: {
          ...obj.infos,
          status: EUploadResponseType.ABORTED,
        },
      });

      this.events.emit('uploadManager_uploadStatusChange', {
        key,
        status: EUploadResponseType.ABORTED,
      });

      this.events.emit(key, {
        status: EUploadResponseType.ABORTED,
      });
    }
    return true;
  }

  addListenersToUpload(key: string, xhr: XMLHttpRequest) {
    const record = this.uploads.get(key);
    if (record === undefined) {
      warn('You must call addUpload before this function');
      return;
    }

    this.uploads.set(key, {
      name: record.name,
      xhr,
      infos: {
        status: EUploadResponseType.ADDED,
        uploadedSize: 0,
        totalSize: 0,
        estimatedTimeLeft: 0,
      },
    });

    xhr.upload.addEventListener(
      'progress',
      (evt: ProgressEvent<XMLHttpRequest>) => {
        const newData = {
          status: EUploadResponseType.PROGRESS,
          totalSize: evt.total,
          estimatedTimeLeft: 0,
          uploadedSize: evt.loaded,
        };

        const uInfos = this.uploads.get(key);
        if (uInfos !== undefined) {
          this.uploads.set(key, {
            xhr,
            name: uInfos.name,
            infos: newData,
          });

          this.events.emit(key, newData);
        }
      },
      true,
    );
  }

  getInfos(key: string): IUploadInfo | undefined {
    const uInfos = this.uploads.get(key);

    if (uInfos === undefined) {
      return undefined;
    }

    return uInfos.infos;
  }

  deleteDownload(key: string) {
    this.uploads.delete(key);

    this.events.emit('uploadManager_uploadStatusChange', {
      key,
      status: EUploadResponseType.DELETED,
    });
  }

  addUpload(name: string): string {
    const Uuid = uuid();

    this.events.emit('uploadManager_uploadStatusChange', {
      status: EUploadResponseType.ADDED,
      key: Uuid,
      name,
    } as IUploadStatus);

    this.uploads.set(Uuid, {
      name,
      xhr: undefined as unknown as XMLHttpRequest,
      infos: {
        status: EUploadResponseType.ADDED,
        uploadedSize: 0,
        totalSize: 0,
        estimatedTimeLeft: 0,
      },
    });

    return Uuid;
  }

  addUploads(names: string[]): string[] {
    const uuids = [] as string[];
    const statuses = [] as IUploadStatus[];
    for (const name of names) {
      const Uuid = uuid();
      uuids.push(Uuid);
      this.uploads.set(Uuid, {
        name,
        xhr: undefined as unknown as XMLHttpRequest,
        infos: {
          status: EUploadResponseType.ADDED,
          uploadedSize: 0,
          totalSize: 0,
          estimatedTimeLeft: 0,
        },
      });

      statuses.push({
        status: EUploadResponseType.ADDED,
        key: Uuid,
        name,
      });
    }

    this.events.emit('uploadManager_uploadStatusChange_multi', statuses);

    return uuids;
  }

  signalSuccess(key: string): boolean {
    const obj = this.uploads.get(key);
    if (obj === undefined) {
      return false;
    } else {
      this.uploads.set(key, {
        ...obj,
        infos: {
          ...obj.infos,
          status: EUploadResponseType.SUCCESS,
        },
      });

      this.events.emit('uploadManager_uploadStatusChange', {
        key,
        status: EUploadResponseType.SUCCESS,
      });

      this.events.emit(key, {
        ...obj,
        status: EUploadResponseType.SUCCESS,
      });
      return true;
    }
  }

  signalFailure(key: string, message?: string): boolean {
    const obj = this.uploads.get(key);
    if (obj === undefined) {
      return false;
    } else {
      this.uploads.set(key, {
        ...obj,
        infos: {
          ...obj.infos,
          status: EUploadResponseType.FAILURE,
          message,
        },
      });

      this.events.emit('uploadManager_uploadStatusChange', {
        key,
        status: EUploadResponseType.FAILURE,
        message,
      });

      this.events.emit(key, {
        ...obj,
        status: EUploadResponseType.FAILURE,
        message,
      });
      return true;
    }
  }

  // Delete all listeners
  cleanup() {
    this.events.removeAllListeners();
  }
}
